
import React from 'react';
import { content } from '../data/content';
import { User, Video, Database } from 'lucide-react';

const Solution: React.FC = () => {
  const { solution } = content;

  const getStepStyles = (index: number) => {
    switch (index) {
      case 0: // 设定人设 - Blue
        return {
          icon: <User className="w-10 h-10" />,
          color: "text-blue-600",
          bg: "bg-blue-50",
          border: "border-blue-100",
          shadow: "shadow-blue-100",
          gradient: "from-blue-50 to-transparent",
          hoverText: "group-hover:text-blue-700",
          numberColor: "group-hover:text-blue-300"
        };
      case 1: // AI 制作 - Purple
        return {
          icon: <Video className="w-10 h-10" />,
          color: "text-purple-600",
          bg: "bg-purple-50",
          border: "border-purple-100",
          shadow: "shadow-purple-100",
          gradient: "from-purple-50 to-transparent",
          hoverText: "group-hover:text-purple-700",
          numberColor: "group-hover:text-purple-300"
        };
      case 2: // 专属库 - Orange
        return {
          icon: <Database className="w-10 h-10" />,
          color: "text-orange-600",
          bg: "bg-orange-50",
          border: "border-orange-100",
          shadow: "shadow-orange-100",
          gradient: "from-orange-50 to-transparent",
          hoverText: "group-hover:text-orange-700",
          numberColor: "group-hover:text-orange-300"
        };
      default:
        return {
          icon: <User className="w-10 h-10" />,
          color: "text-blue-600",
          bg: "bg-blue-50",
          border: "border-blue-100",
          shadow: "shadow-blue-100",
          gradient: "from-blue-50 to-transparent",
          hoverText: "group-hover:text-blue-700",
          numberColor: "group-hover:text-blue-300"
        };
    }
  };

  return (
    <section id="solution" className="min-h-screen flex items-center justify-center py-24 bg-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-blue-50/50 to-transparent -z-10"></div>
      <div className="absolute bottom-0 left-0 w-1/2 h-full bg-gradient-to-r from-purple-50/50 to-transparent -z-10"></div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center max-w-5xl mx-auto mb-24">
          {/* Main Title - Huge */}
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-8 tracking-tight animate-fade-in-up">
            {solution.title}
          </h2>
          
          {/* Subtitle - Large & Gradient */}
          <h3 className="text-2xl md:text-4xl font-bold mb-16 leading-tight tracking-tight animate-fade-in-up delay-100 text-transparent bg-clip-text bg-gradient-to-r from-primary-600 via-purple-600 to-primary-600 animate-gradient bg-[length:200%_auto]">
            {solution.subtitle}
          </h3>
          
          {/* Quote - Redesigned to stand out */}
          <div className="relative inline-block animate-fade-in-up delay-200 max-w-4xl mx-auto">
            <div className="absolute -inset-4 bg-gradient-to-r from-blue-100/50 to-purple-100/50 rounded-2xl blur-xl opacity-70"></div>
            <p className="relative text-2xl md:text-3xl font-bold text-gray-800 leading-relaxed bg-white/80 backdrop-blur-sm px-8 py-6 rounded-2xl border border-white/50 shadow-sm">
              {solution.quote}
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {solution.steps.map((step, index) => {
            const styles = getStepStyles(index);
            return (
              <div 
                key={step.id} 
                className={`group relative bg-white rounded-[2rem] p-8 border transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl ${styles.border} ${styles.shadow}`}
              >
                {/* Hover Gradient Background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${styles.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-[2rem]`}></div>
                
                <div className="relative z-10">
                  {/* Header */}
                  <div className="flex justify-between items-start mb-8">
                    <div className={`w-20 h-20 rounded-2xl ${styles.bg} flex items-center justify-center transform group-hover:scale-110 transition-transform duration-500 shadow-sm`}>
                      <div className={`${styles.color}`}>
                        {styles.icon}
                      </div>
                    </div>
                    <span className={`text-6xl font-bold opacity-10 transition-colors duration-500 ${styles.numberColor}`}>
                      0{step.id}
                    </span>
                  </div>

                  {/* Content */}
                  <h4 className={`text-2xl font-bold text-gray-900 mb-4 transition-colors duration-300 ${styles.hoverText}`}>
                    {step.title}
                  </h4>
                  <p className="text-gray-500 leading-relaxed text-lg">
                    {step.text}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Solution;
